// src/lib/guided/lints.ts
//
// Phase 2.5 of docs/guided-shopping_plan.md: the 17 §3.14 rules
// (docs/guided-shopping_research.md §3.14) plus the sales-science lints
// docs/sales-methodologies_research.md §2.4/§2.6/§3.5/§4.7 add. `lintFunnel`
// checks a compiled flow and returns every finding; `compileFunnel`
// (compile.ts) calls it and throws on any error-level one, so a flow that
// fails a hard rule is never returned to a caller. Warning-level findings are
// for the owner (Phase 4's plain-words lint display, task 4.4), never a
// block.
//
// Circular with compile.ts by necessity: this file reads compile.ts's
// `isFactualCriterion`/`NOT_SURE_YET_OPTION`/`URGENCY_FAMILY_TYPES`/
// `NEED_CRITERION_ID`, and compile.ts calls this file's `lintFunnel`. Safe
// under Node ESM only because every cross-module reference sits inside a
// function body, never at either module's own top level -- see
// `.wolf/cerebrum.md`'s 2026-09-24 entry and compile.ts's own header for the
// TDZ trap this avoids.
//
// Two rules are deliberately not enforced yet, both cited at their function:
// rule 4 (data minimization) only catches the one unambiguous case the
// current schema can prove (a `fit` criterion absent from every offer's
// `offerFit`), because the wider "or changes no grade" half needs Phase 3's
// `grade.ts`, which does not exist yet; rule 13 (result price band vs quote
// band) has no data to check against at all -- `FunnelOffer` and
// `FormOutcome` carry no price field anywhere in the schema -- and is a
// deliberate no-op until one exists.
//
// 2.9 adds two more, against `config.concerns` (1.4) rather than a compiled
// field: an error when a seed placeholder (the "[...]" convention
// presets.ts's `notFit` seed already uses) survives into a published
// answer, and a warning when an answer cites a number, a certification or
// "guarantee" the owner should confirm before it goes out (research §6.4,
// §8's "claims we must not make"). The plan's third 2.9 bullet, "no answer
// may offer a discount the owner did not write", has no lint of its own:
// nothing in this file or compile.ts generates concern text, so there is no
// code path to check against, and compile.test.ts's "plan 2.9" block pins
// that a compiled field's `concerns` is the owner's array, unmodified.
import type { CriterionType, FunnelConfig, FunnelCriterion } from "./config";
import type { CompiledFunnel } from "./compile";
import type { FieldOption, FormOutcome, TypedFormField } from "@/lib/types";
import { getVisibleFields } from "@/lib/utils/logicEngine";
import {
  isFactualCriterion,
  NEED_CRITERION_ID,
  NOT_SURE_YET_OPTION,
  URGENCY_FAMILY_TYPES,
} from "./compile";

export type LintLevel = "error" | "warning";

export type LintRuleId =
  | "too-many-options"
  | "multi-select-or-grid"
  | "not-sure-yet-on-non-factual"
  | "no-effect-on-outcome"
  | "near-opposite-options"
  | "confirmshaming-decline"
  | "scarcity-or-activity-wording"
  | "preselected-option"
  | "path-too-long"
  | "agitation-or-fear-wording"
  | "stakes-wording"
  | "unbacked-norm-claim"
  | "price-band-mismatch"
  | "hypothetical-question"
  | "duplicate-urgency-question"
  | "too-many-fit-questions"
  | "implication-question"
  | "concern-placeholder-answer"
  | "concern-answer-needs-confirmation";

export interface LintFinding {
  rule: LintRuleId;
  level: LintLevel;
  /** Plain language for a non-technical owner (CLAUDE.md §0a). */
  message: string;
  /** The compiled field or criterion this finding is about, when there is one. */
  fieldId?: string;
}

function optionLabel(option: FieldOption): string {
  return typeof option.label === "string" ? option.label : String(option.label);
}

interface ScannedText {
  text: string;
  fieldId?: string;
}

/** Every compiled question's own label and its "why we ask" helpText. */
function questionTexts(fields: readonly TypedFormField[]): ScannedText[] {
  return fields
    .filter(
      (field) => typeof field.label === "string" && field.label.length > 0,
    )
    .map((field) => ({ text: field.label, fieldId: field.id }));
}

/**
 * Questions and result-page copy: where research §2.6 item 5 and §3.14 rule
 * 10 say agitation, stakes and unbacked norm claims may not appear ("in the
 * opener or the result", "in a question or result copy"). Deliberately
 * excludes answer-option labels, which rule 6 (confirmshaming) and rule 7
 * (scarcity/activity) check separately.
 */
function questionAndResultTexts(
  config: FunnelConfig,
  compiled: CompiledFunnel,
): ScannedText[] {
  const texts: ScannedText[] = [...questionTexts(compiled.fields)];
  for (const field of compiled.fields) {
    if (field.helpText) texts.push({ text: field.helpText, fieldId: field.id });
  }
  for (const outcome of compiled.outcomes) {
    texts.push(...outcomeTexts(outcome));
  }
  texts.push({ text: config.notFit.text });
  return texts;
}

function outcomeTexts(outcome: FormOutcome): ScannedText[] {
  const texts: ScannedText[] = [
    { text: outcome.title },
    { text: outcome.body },
  ];
  if (outcome.cta) texts.push({ text: outcome.cta.label });
  return texts;
}

/** Every answer-option label across every compiled question. */
function optionTexts(fields: readonly TypedFormField[]): ScannedText[] {
  const texts: ScannedText[] = [];
  for (const field of fields) {
    for (const option of field.options ?? []) {
      texts.push({ text: optionLabel(option), fieldId: field.id });
    }
  }
  return texts;
}

function pushIfMatch(
  texts: readonly ScannedText[],
  patterns: readonly RegExp[],
  rule: LintRuleId,
  level: LintLevel,
  describe: (text: string) => string,
  findings: LintFinding[],
): void {
  for (const { text, fieldId } of texts) {
    if (patterns.some((pattern) => pattern.test(text))) {
      findings.push({ rule, level, message: describe(text), fieldId });
    }
  }
}

// -- Rule 1 (options per question), 2 (multi-select/grid), 8 (pre-selected) --

export const HARD_CAP_OPTIONS = 7;
export const DEFAULT_CAP_OPTIONS = 5;

function lintFieldStructure(
  fields: readonly TypedFormField[],
  findings: LintFinding[],
): void {
  for (const field of fields) {
    const count = field.options?.length ?? 0;
    if (count > HARD_CAP_OPTIONS) {
      findings.push({
        rule: "too-many-options",
        level: "error",
        message: `"${field.label}" offers ${count} answers; the hard ceiling is ${HARD_CAP_OPTIONS} (§3.14 rule 1).`,
        fieldId: field.id,
      });
    } else if (count > DEFAULT_CAP_OPTIONS) {
      findings.push({
        rule: "too-many-options",
        level: "warning",
        message: `"${field.label}" offers ${count} answers; keep it to ${DEFAULT_CAP_OPTIONS} where possible (§3.14 rule 1).`,
        fieldId: field.id,
      });
    }

    // No compiled field is a grid today (§3.14 rule 2's other half): nothing
    // in the schema represents a grid layout, so there is nothing to check
    // beyond the multi-select half here.
    if (field.multiple || field.type === "checkbox") {
      findings.push({
        rule: "multi-select-or-grid",
        level: "error",
        message: `"${field.label}" is a multi-select; use single-select, or yes/no pairs (§3.14 rule 2).`,
        fieldId: field.id,
      });
    }

    const defaultValue = field.defaultValue;
    const isPreselected =
      defaultValue !== null &&
      defaultValue !== undefined &&
      defaultValue !== "" &&
      !(Array.isArray(defaultValue) && defaultValue.length === 0);
    if (isPreselected) {
      findings.push({
        rule: "preselected-option",
        level: "error",
        message: `"${field.label}" has a pre-selected answer; nothing may be chosen for the visitor (§3.14 rule 10).`,
        fieldId: field.id,
      });
    }
  }
}

// -- Rule 3: "Not sure yet" only on factual qualifiers --

function lintNotSureYetPlacement(
  config: FunnelConfig,
  fields: readonly TypedFormField[],
  findings: LintFinding[],
): void {
  const criteriaById = new Map(config.criteria.map((c) => [c.id, c]));
  for (const field of fields) {
    if (field.id === NEED_CRITERION_ID) continue; // no owner-authored options
    const criterion = criteriaById.get(field.id);
    if (!criterion) continue;
    const hasNotSureYet = (field.options ?? []).some(
      (option) =>
        optionLabel(option).trim().toLowerCase() ===
        NOT_SURE_YET_OPTION.label.toLowerCase(),
    );
    if (hasNotSureYet && !isFactualCriterion(criterion.type)) {
      findings.push({
        rule: "not-sure-yet-on-non-factual",
        level: "error",
        message: `"${field.label}" offers "Not sure yet" but is not a factual qualifier (budget, timeline or decision maker); that hedge belongs only there (§3.14 rule 3).`,
        fieldId: field.id,
      });
    }
  }
}

// -- Rule 4: data minimization (the provable half) --

/**
 * A `fit` criterion the compiler kept but that appears in no offer's
 * `offerFit` at all cannot affect which offer is reached (config.ts's own
 * doc comment: absent from `offerFit` means "not affecting this offer's
 * fit", for every offer). That is `fit`'s one documented purpose
 * (docs/sales-methodologies_research.md §1.5, "Fit / Target profile"), so
 * this is the one type/condition pair provable without Phase 3's grade
 * table. Scoped to configs with more than one offer: with a single offer
 * there is no "which offer" to affect, so this half of the rule is moot and
 * the wider "or changes no grade" half is not yet checkable.
 */
function lintDataMinimization(
  config: FunnelConfig,
  fields: readonly TypedFormField[],
  findings: LintFinding[],
): void {
  if (config.offers.length <= 1) return;
  const offerFit = config.offerFit ?? {};
  const criteriaById = new Map(config.criteria.map((c) => [c.id, c]));
  for (const field of fields) {
    const criterion = criteriaById.get(field.id);
    if (!criterion || criterion.type !== "fit") continue;
    const mentioned = Object.values(offerFit).some(
      (entry) => criterion.id in entry,
    );
    if (!mentioned) {
      findings.push({
        rule: "no-effect-on-outcome",
        level: "warning",
        message: `"${field.label}" does not affect which offer any visitor reaches; drop it or wire it into Offer fit (§3.14 rule 11, data minimization).`,
        fieldId: field.id,
      });
    }
  }
}

// -- Rule 5: near-opposite options on one question --

const ANTONYM_PAIRS: readonly [string, string][] = [
  ["start", "cancel"],
  ["upgrade", "downgrade"],
  ["accept", "decline"],
  ["enable", "disable"],
  ["begin", "end"],
  ["add", "remove"],
  ["open", "close"],
  ["increase", "decrease"],
];

function lintNearOppositeOptions(
  fields: readonly TypedFormField[],
  findings: LintFinding[],
): void {
  for (const field of fields) {
    const labels = (field.options ?? []).map((option) =>
      optionLabel(option).toLowerCase(),
    );
    for (const [a, b] of ANTONYM_PAIRS) {
      const aPattern = new RegExp(`\\b${a}\\b`, "i");
      const bPattern = new RegExp(`\\b${b}\\b`, "i");
      if (
        labels.some((l) => aPattern.test(l)) &&
        labels.some((l) => bPattern.test(l))
      ) {
        findings.push({
          rule: "near-opposite-options",
          level: "warning",
          message: `"${field.label}" pairs near-opposite answers ("${a}" / "${b}"); reword so they don't read as accept/reject (§3.14 rule 5).`,
          fieldId: field.id,
        });
      }
    }
  }
}

// -- Rule 6: confirmshaming decline labels --

const CONFIRMSHAME_PATTERN =
  /^no,?\s+i\s+(don'?t|do\s+not|won'?t|will\s+not)\b/i;

function lintConfirmshaming(
  fields: readonly TypedFormField[],
  findings: LintFinding[],
): void {
  for (const { text, fieldId } of optionTexts(fields)) {
    if (CONFIRMSHAME_PATTERN.test(text.trim())) {
      findings.push({
        rule: "confirmshaming-decline",
        level: "error",
        message: `"${text}" is a confirmshaming label (a first-person put-down); use a neutral decline (§3.14 rule 10).`,
        fieldId,
      });
    }
  }
}

// -- Rule 7: countdown, scarcity or fake activity wording --

const SCARCITY_PATTERNS: readonly RegExp[] = [
  /\bonly\s+\d+\s+(left|remaining|spots?|places?)\b/i,
  /\b\d+\s+(people|customers|businesses)\s+(booked|bought|signed up|joined)\b/i,
  /\boffer\s+ends?\b/i,
  /\bact\s+now\b/i,
  /\bhurry\b/i,
  /\blimited\s+time\b/i,
];

// -- Rule 10: agitation or fear wording --

const AGITATION_PATTERNS: readonly RegExp[] = [
  /before\s+it'?s?\s+too\s+late/i,
  /you\s+(could|will|might)\s+lose\s+everything/i,
];

// -- Rule 11: "stakes" copy --

const STAKES_PATTERNS: readonly RegExp[] = [
  /you'?ll\s+lose\s+[\w-]+(\s+[\w-]+){0,3}\s+if\b/i,
];

// -- Rule 12: unbacked norm claims --
//
// Always warns: nothing in the 1.1 schema lets an owner mark a claim as
// sourced yet, so there is no "unless sourced" exemption to check against.
// The safe default when a flag is missing is the one CLAUDE.md §2 asks for.

const NORM_CLAIM_PATTERNS: readonly RegExp[] = [
  /\bmost\s+(businesses|customers|people|clients)\s+like\s+yours?\b/i,
];

// -- Rule 14: hypothetical questions (the Mom Test) --

const HYPOTHETICAL_PATTERNS: readonly RegExp[] = [
  /\bwould\s+you\s+(pay|buy|use|consider|be\s+interested)\b/i,
  /\bhow\s+likely\s+(are|would)\s+you\b/i,
];

// -- Rule 17: implication questions --

const IMPLICATION_PATTERNS: readonly RegExp[] = [
  /\bwhat\s+(will|would)\s+(it|this)\s+cost\s+you\s+if\b/i,
  /\bwhat\s+happens\s+if\s+you\s+don'?t\b/i,
];

// -- Rule 9: no path longer than maxQuestions --

/**
 * Every leaf's path length through a compiled flow, walked with the same
 * `getVisibleFields` every renderer calls (mirrors `tests/guided/compile.
 * test.ts`'s own `longestPath` helper, which 2.1/2.4 already use to pin
 * "asks at most N questions"). One shared walker for `longestPathLength`
 * below and 5.2's `fixedPathLength`, so the two can never disagree on what
 * counts as a leaf.
 */
function leafPathLengths(fields: readonly TypedFormField[]): number[] {
  const lengths: number[] = [];
  function walk(values: Record<string, unknown>, asked: number): void {
    const visible = getVisibleFields(fields as TypedFormField[], values);
    const next = visible.find(
      (f) => f.options && f.options.length > 0 && values[f.name] === undefined,
    );
    if (!next) {
      lengths.push(asked);
      return;
    }
    for (const option of next.options ?? []) {
      walk({ ...values, [next.name]: option.value }, asked + 1);
    }
  }
  walk({}, 0);
  return lengths;
}

/**
 * The number of button questions on the longest path through a compiled
 * flow. `planSteps` already bounds every path to `config.maxQuestions` by
 * construction; this is the guard-rail lint that catches a future
 * regression in that bound, not a rule that should ever fire against
 * `compileFunnel`'s own output today.
 *
 * Exported (plan 5.6) for `upFrontScreen.ts`'s "N quick questions" promise
 * on screen 1: the tightest honest number is the longest path any visitor
 * could actually be asked, never `config.maxQuestions` (an owner-set
 * ceiling `planSteps` may stop well short of, per the accountant seed's own
 * 1-or-5 branch).
 */
export function longestPathLength(fields: readonly TypedFormField[]): number {
  return Math.max(...leafPathLengths(fields));
}

/**
 * Plan 5.2, §3.14 rule 5: "show a step count only when the path length is
 * fixed. Otherwise say 'a few quick questions'." Returns the shared length
 * when every leaf takes exactly the same number of questions to reach, or
 * `null` when branches differ in length -- the renderer's own signal for
 * which of the two to show. Exported for `GuidedFunnelRenderer.tsx` and
 * `kiroku-embed.ts`'s `GuidedFunnelWidget`, which both need the same answer
 * for the same compiled flow (bug-037's "one evaluator, many renderers"
 * discipline applies here too, even though this is a display decision
 * rather than a visibility one).
 */
export function fixedPathLength(
  fields: readonly TypedFormField[],
): number | null {
  const [first, ...rest] = leafPathLengths(fields);
  return first !== undefined && rest.every((length) => length === first)
    ? first
    : null;
}

function lintPathLength(
  config: FunnelConfig,
  compiled: CompiledFunnel,
  findings: LintFinding[],
): void {
  const longest = longestPathLength(compiled.fields);
  if (longest > config.maxQuestions) {
    findings.push({
      rule: "path-too-long",
      level: "error",
      message: `The longest path asks ${longest} questions; maxQuestions is ${config.maxQuestions} (§3.14 rule 9).`,
    });
  }
}

// -- Rule 15: the urgency family asks the same thing twice --

function lintDuplicateUrgencyQuestions(
  config: FunnelConfig,
  findings: LintFinding[],
): void {
  const byType = new Map<CriterionType, FunnelCriterion[]>();
  for (const criterion of config.criteria) {
    if (
      !(URGENCY_FAMILY_TYPES as readonly CriterionType[]).includes(
        criterion.type,
      )
    ) {
      continue;
    }
    const list = byType.get(criterion.type) ?? [];
    list.push(criterion);
    byType.set(criterion.type, list);
  }
  for (const list of byType.values()) {
    if (list.length <= 1) continue;
    for (const criterion of list) {
      findings.push({
        rule: "duplicate-urgency-question",
        level: "error",
        message: `${list.length} "${list[0].type}" questions ask about the same thing (${list
          .map((c) => `"${c.question}"`)
          .join(", ")}); ask intent once (Morwitz et al.).`,
        fieldId: criterion.id,
      });
    }
  }
}

// -- Rule 16: at most two situation/fit questions on one path (SPIN) --

export const MAX_FIT_QUESTIONS = 2;

/**
 * A global count of `fit`-type criteria, not a true per-path count:
 * enumerating every path's own fit-question count needs the reachability
 * walker 2.6 is about to build. This conservative approximation never
 * under-flags (a path can never carry more fit questions than the whole
 * flow has), and it is exact whenever the flow has no branching between
 * distinct `fit` criteria, which is every seed template 2.7 ships.
 */
function lintTooManyFitQuestions(
  config: FunnelConfig,
  findings: LintFinding[],
): void {
  const fitCriteria = config.criteria.filter((c) => c.type === "fit");
  if (fitCriteria.length > MAX_FIT_QUESTIONS) {
    findings.push({
      rule: "too-many-fit-questions",
      level: "error",
      message: `${fitCriteria.length} fit/situation questions in this flow; keep it to ${MAX_FIT_QUESTIONS} on any one path (SPIN).`,
    });
  }
}

// -- Plan 2.9: seed placeholders and unconfirmed claims in concern answers --

/**
 * The bracket-placeholder convention this codebase already uses for an
 * unfilled seed (presets.ts's `notFit.text`: "[Write what to say to people
 * who aren't a good fit]"; 1.4's own doc comment names the same convention
 * for concern seeds, "[Your answer about price]"). Any survivor in a
 * published answer means the owner never replaced it.
 */
const CONCERN_PLACEHOLDER_PATTERN = /\[[^\]]+\]/;

/** research §6.4/§8: a number, certification or guarantee the owner should confirm. */
const CONCERN_CONFIRM_PATTERNS: readonly RegExp[] = [
  /\d+(\.\d+)?\s*%/, // a percentage figure ("cut workflow time by 70%")
  /\b\d+(\.\d+)?\s*x\b/i, // a multiplier figure ("3.4x ROI")
  /\b(SOC\s?2|ISO\s?27001|HIPAA|GDPR-compliant)\b/i, // a certification name
  /\bguarantee(d|s)?\b/i,
];

function lintConcernAnswers(
  config: FunnelConfig,
  findings: LintFinding[],
): void {
  for (const concern of config.concerns ?? []) {
    if (CONCERN_PLACEHOLDER_PATTERN.test(concern.answer)) {
      findings.push({
        rule: "concern-placeholder-answer",
        level: "error",
        message: `"${concern.label}"'s answer still has a seed placeholder ("${concern.answer}"); write the real answer before publishing.`,
        fieldId: concern.id,
      });
    }
    if (
      CONCERN_CONFIRM_PATTERNS.some((pattern) => pattern.test(concern.answer))
    ) {
      findings.push({
        rule: "concern-answer-needs-confirmation",
        level: "warning",
        message: `"${concern.label}"'s answer ("${concern.answer}") cites a number, certification or guarantee; confirm it's accurate before publishing (§8).`,
        fieldId: concern.id,
      });
    }
  }
}

/**
 * Checks a compiled flow against every §3.14 rule the plan lists.
 * `compileFunnel` calls this and throws on any error-level finding; nothing
 * else in the codebase should need to call it directly except a caller that
 * wants to show the owner the warning-level findings (Phase 4).
 */
export function lintFunnel(
  config: FunnelConfig,
  compiled: CompiledFunnel,
): LintFinding[] {
  const findings: LintFinding[] = [];

  lintFieldStructure(compiled.fields, findings);
  lintNotSureYetPlacement(config, compiled.fields, findings);
  lintDataMinimization(config, compiled.fields, findings);
  lintNearOppositeOptions(compiled.fields, findings);
  lintConfirmshaming(compiled.fields, findings);
  lintPathLength(config, compiled, findings);
  lintDuplicateUrgencyQuestions(config, findings);
  lintTooManyFitQuestions(config, findings);
  lintConcernAnswers(config, findings);

  const questionAndResult = questionAndResultTexts(config, compiled);
  const allWordingTexts = [
    ...questionAndResult,
    ...optionTexts(compiled.fields),
  ];

  pushIfMatch(
    allWordingTexts,
    SCARCITY_PATTERNS,
    "scarcity-or-activity-wording",
    "error",
    (text) =>
      `"${text}" reads as a countdown, scarcity or fake-activity claim (§3.14 rule 10).`,
    findings,
  );
  pushIfMatch(
    questionAndResult,
    AGITATION_PATTERNS,
    "agitation-or-fear-wording",
    "error",
    (text) =>
      `"${text}" reads as agitation or fear wording (sales research §2.4).`,
    findings,
  );
  pushIfMatch(
    questionAndResult,
    STAKES_PATTERNS,
    "stakes-wording",
    "error",
    (text) => `"${text}" reads as "stakes" copy (sales research §2.4).`,
    findings,
  );
  pushIfMatch(
    questionAndResult,
    NORM_CLAIM_PATTERNS,
    "unbacked-norm-claim",
    "warning",
    (text) => `"${text}" makes an unbacked norm claim (sales research §2.4).`,
    findings,
  );

  const questions = questionTexts(compiled.fields);
  pushIfMatch(
    questions,
    HYPOTHETICAL_PATTERNS,
    "hypothetical-question",
    "error",
    (text) => `"${text}" is a hypothetical question (the Mom Test).`,
    findings,
  );
  pushIfMatch(
    questions,
    IMPLICATION_PATTERNS,
    "implication-question",
    "error",
    (text) =>
      `"${text}" is an implication question; implications belong only in owner-written result copy (sales research §4.7).`,
    findings,
  );

  // Rule 13 (result price band vs quote band) is a deliberate no-op: see
  // this file's header comment. Nothing in `FunnelOffer` or `FormOutcome`
  // carries a price band to compare.

  return findings;
}
